import { useState, Component } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

// Error boundary to prevent crashes from breaking the app
class UserTypeDashboardsErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.log("UserTypeDashboards error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // Render nothing if there's an error
            return null;
        }
        return this.props.children;
    }
}

/**
 * Component for displaying user-type-specific dashboards
 * Extensible to support multiple user types (dentist, etc.)
 */
function UserTypeDashboards({ userType, onPromptClick, user }) {
    const { t } = useTranslation();

    // TEST ONLY: Industry switcher for testing
    const [testIndustry, setTestIndustry] = useState(null);

    // Helper function to normalize industry names (remove accents, spaces, etc.)
    const normalizeIndustryKey = (industryName) => {
        // Defensive: ensure we have a valid string
        if (!industryName || typeof industryName !== "string") {
            return "other";
        }
        return industryName
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // Remove accents
            .replace(/\s+/g, "") // Remove spaces
            .replace(/&/g, "y") // Replace & with y (for "Media & Entertainment" -> "mediasyentretenimiento")
            .replace(/[^a-z0-9]/g, ""); // Remove any remaining special characters
    };

    // Helper function to map industry to translation key
    const getIndustryKey = (industryName) => {
        const industryKey = normalizeIndustryKey(industryName);
        const industryMap = {
            // English industry names
            technology: "technology",
            healthcare: "healthcare",
            finance: "finance",
            retail: "retail",
            manufacturing: "manufacturing",
            education: "education",
            realestate: "realEstate",
            legal: "legal",
            consulting: "consulting",
            mediaentertainment: "mediaEntertainment",
            mediaandentertainment: "mediaEntertainment",
            transportation: "transportation",
            agriculture: "agriculture",
            nonprofit: "nonProfit",
            government: "government",
            // Spanish industry names
            tecnologia: "technology",
            salud: "healthcare",
            finanzas: "finance",
            manufactura: "manufacturing",
            educacion: "education",
            bienesraices: "realEstate",
            consultoria: "consulting",
            mediosyentretenimiento: "mediaEntertainment",
            transporte: "transportation",
            agricultura: "agriculture",
            sinfinesdelucro: "nonProfit",
            gobierno: "government",
            otro: "other",
        };
        return industryMap[industryKey] || "other";
    };

    // Dental Practice Dashboards
    if (userType === "dentist") {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Practice Analytics Dashboard */}
                <div
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.dentist.practiceAnalytics.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Icon
                                name="BarChart3"
                                className="h-5 w-5 text-blue-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.practiceAnalytics.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.practiceAnalytics.description"
                                )}
                            </p>
                            <div className="text-xs text-blue-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Appointments & Cancellations */}
                <div
                    className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.dentist.appointmentsCancellations.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-red-100 p-2 rounded-lg group-hover:bg-red-200 transition-colors">
                            <Icon
                                name="Calendar"
                                className="h-5 w-5 text-red-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.appointmentsCancellations.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.appointmentsCancellations.description"
                                )}
                            </p>
                            <div className="text-xs text-red-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Patient Records */}
                <div
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.dentist.patientRecords.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200 transition-colors">
                            <Icon
                                name="FileText"
                                className="h-5 w-5 text-green-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.patientRecords.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.patientRecords.description"
                                )}
                            </p>
                            <div className="text-xs text-green-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Treatment Planning */}
                <div
                    className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.dentist.treatmentPlanning.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <Icon
                                name="ClipboardCheck"
                                className="h-5 w-5 text-purple-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.treatmentPlanning.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.treatmentPlanning.description"
                                )}
                            </p>
                            <div className="text-xs text-purple-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Revenue & Billing */}
                <div
                    className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.dentist.revenueBilling.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-amber-100 p-2 rounded-lg group-hover:bg-amber-200 transition-colors">
                            <Icon
                                name="DollarSign"
                                className="h-5 w-5 text-amber-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.revenueBilling.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.dentist.revenueBilling.description"
                                )}
                            </p>
                            <div className="text-xs text-amber-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Property Management Dashboards
    if (userType === "property_dashboard") {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Property Portfolio Overview */}
                <div
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.propertyPortfolioOverview.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Icon
                                name="Building2"
                                className="h-5 w-5 text-blue-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.propertyPortfolioOverview.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.propertyPortfolioOverview.description"
                                )}
                            </p>
                            <div className="text-xs text-blue-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tenant Management */}
                <div
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.tenantManagement.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-green-100 p-2 rounded-lg group-hover:bg-green-200 transition-colors">
                            <Icon
                                name="Users"
                                className="h-5 w-5 text-green-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.tenantManagement.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.tenantManagement.description"
                                )}
                            </p>
                            <div className="text-xs text-green-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Maintenance Requests */}
                <div
                    className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.maintenanceRequests.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-orange-100 p-2 rounded-lg group-hover:bg-orange-200 transition-colors">
                            <Icon
                                name="Wrench"
                                className="h-5 w-5 text-orange-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.maintenanceRequests.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.maintenanceRequests.description"
                                )}
                            </p>
                            <div className="text-xs text-orange-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Rent Collection & Financials */}
                <div
                    className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.rentCollectionFinancials.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-emerald-100 p-2 rounded-lg group-hover:bg-emerald-200 transition-colors">
                            <Icon
                                name="DollarSign"
                                className="h-5 w-5 text-emerald-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.rentCollectionFinancials.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.rentCollectionFinancials.description"
                                )}
                            </p>
                            <div className="text-xs text-emerald-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Occupancy & Vacancy Rates */}
                <div
                    className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.occupancyVacancyRates.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <Icon
                                name="BarChart3"
                                className="h-5 w-5 text-purple-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.occupancyVacancyRates.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.occupancyVacancyRates.description"
                                )}
                            </p>
                            <div className="text-xs text-purple-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lease Management */}
                <div
                    className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.propertyDashboard.leaseManagement.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-rose-100 p-2 rounded-lg group-hover:bg-rose-200 transition-colors">
                            <Icon
                                name="FileText"
                                className="h-5 w-5 text-rose-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.leaseManagement.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.propertyDashboard.leaseManagement.description"
                                )}
                            </p>
                            <div className="text-xs text-rose-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Fleet Owner Dashboards
    if (userType === "fleet_owner") {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fleet Overview */}
                <div
                    className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.fleetOwner.fleetOverview.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                            <Icon
                                name="Truck"
                                className="h-5 w-5 text-blue-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fleetOverview.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fleetOverview.description"
                                )}
                            </p>
                            <div className="text-xs text-blue-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Maintenance & Repairs */}
                <div
                    className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.fleetOwner.maintenanceRepairs.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-orange-100 p-2 rounded-lg group-hover:bg-orange-200 transition-colors">
                            <Icon
                                name="Wrench"
                                className="h-5 w-5 text-orange-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.maintenanceRepairs.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.maintenanceRepairs.description"
                                )}
                            </p>
                            <div className="text-xs text-orange-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Driver Management */}
                <div
                    className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.fleetOwner.driverManagement.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <Icon
                                name="Users"
                                className="h-5 w-5 text-purple-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.driverManagement.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.driverManagement.description"
                                )}
                            </p>
                            <div className="text-xs text-purple-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fuel Consumption */}
                <div
                    className="bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.fleetOwner.fuelConsumption.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-red-100 p-2 rounded-lg group-hover:bg-red-200 transition-colors">
                            <Icon
                                name="Droplet"
                                className="h-5 w-5 text-red-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fuelConsumption.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fuelConsumption.description"
                                )}
                            </p>
                            <div className="text-xs text-red-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fleet Analytics */}
                <div
                    className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
                    onClick={() =>
                        onPromptClick(
                            t(
                                "chatPage.userTypeDashboards.fleetOwner.fleetAnalytics.prompt"
                            )
                        )
                    }
                >
                    <div className="flex items-start gap-3">
                        <div className="bg-teal-100 p-2 rounded-lg group-hover:bg-teal-200 transition-colors">
                            <Icon
                                name="BarChart3"
                                className="h-5 w-5 text-teal-600"
                            />
                        </div>
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fleetAnalytics.title"
                                )}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3">
                                {t(
                                    "chatPage.userTypeDashboards.fleetOwner.fleetAnalytics.description"
                                )}
                            </p>
                            <div className="text-xs text-teal-600 font-medium">
                                {t(
                                    "chatPage.userTypeDashboards.clickToCreateDashboard"
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Excel Reports Dashboards - Industry-specific
    if (userType === "excel_reports") {
        const industry = user?.industry || "other";

        // Helper function to get industry-specific dashboard config
        const getIndustryDashboards = () => {
            const basePath = "chatPage.userTypeDashboards.excelReports";
            const mappedIndustry = getIndustryKey(industry);

            // Return dashboard keys for the industry
            const dashboards = [
                {
                    key: "salesPerformance",
                    icon: "TrendingUp",
                    color: "blue",
                },
                {
                    key: "financialAnalysis",
                    icon: "DollarSign",
                    color: "green",
                },
                {
                    key: "operationalMetrics",
                    icon: "BarChart3",
                    color: "purple",
                },
                {
                    key: "customerAnalytics",
                    icon: "Users",
                    color: "indigo",
                },
                {
                    key: "inventoryManagement",
                    icon: "Package",
                    color: "orange",
                },
                {
                    key: "performanceReports",
                    icon: "FileText",
                    color: "teal",
                },
            ];

            return dashboards.map((dashboard) => ({
                ...dashboard,
                translationKey: `${basePath}.${mappedIndustry}.${dashboard.key}`,
            }));
        };

        const dashboards = getIndustryDashboards();

        const colorConfigs = {
            blue: {
                gradient: "from-blue-50 to-indigo-50",
                border: "border-blue-200",
                iconBg: "bg-blue-100",
                iconText: "text-blue-600",
                textColor: "text-blue-600",
            },
            green: {
                gradient: "from-green-50 to-emerald-50",
                border: "border-green-200",
                iconBg: "bg-green-100",
                iconText: "text-green-600",
                textColor: "text-green-600",
            },
            purple: {
                gradient: "from-purple-50 to-violet-50",
                border: "border-purple-200",
                iconBg: "bg-purple-100",
                iconText: "text-purple-600",
                textColor: "text-purple-600",
            },
            indigo: {
                gradient: "from-indigo-50 to-blue-50",
                border: "border-indigo-200",
                iconBg: "bg-indigo-100",
                iconText: "text-indigo-600",
                textColor: "text-indigo-600",
            },
            orange: {
                gradient: "from-orange-50 to-amber-50",
                border: "border-orange-200",
                iconBg: "bg-orange-100",
                iconText: "text-orange-600",
                textColor: "text-orange-600",
            },
            teal: {
                gradient: "from-teal-50 to-cyan-50",
                border: "border-teal-200",
                iconBg: "bg-teal-100",
                iconText: "text-teal-600",
                textColor: "text-teal-600",
            },
        };

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dashboards.map((dashboard) => {
                    const colors =
                        colorConfigs[dashboard.color] || colorConfigs.blue;

                    return (
                        <div
                            key={dashboard.key}
                            className={`bg-gradient-to-br ${colors.gradient} ${colors.border} rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group`}
                            onClick={() =>
                                onPromptClick(
                                    t(`${dashboard.translationKey}.prompt`, {
                                        defaultValue: `Create an Excel dashboard for ${industry} ${dashboard.key}`,
                                    })
                                )
                            }
                        >
                            <div className="flex items-start gap-3">
                                <div
                                    className={`${colors.iconBg} p-2 rounded-lg group-hover:opacity-80 transition-colors`}
                                >
                                    <Icon
                                        name={dashboard.icon}
                                        className={`h-5 w-5 ${colors.iconText}`}
                                    />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-gray-900 mb-1">
                                        {t(
                                            `${dashboard.translationKey}.title`,
                                            {
                                                defaultValue: dashboard.key
                                                    .replace(/([A-Z])/g, " $1")
                                                    .trim(),
                                            }
                                        )}
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-3">
                                        {t(
                                            `${dashboard.translationKey}.description`,
                                            {
                                                defaultValue: `View and analyze ${dashboard.key} for ${industry} industry`,
                                            }
                                        )}
                                    </p>
                                    <div
                                        className={`text-xs ${colors.textColor} font-medium`}
                                    >
                                        {t(
                                            "chatPage.userTypeDashboards.clickToCreateDashboard"
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Default: Industry-specific Web Apps, Websites, and Dashboards
    const industry = testIndustry || user?.industry || "other";
    const mappedIndustry = getIndustryKey(industry);
    const basePath = "chatPage.defaultOptions";

    // TEST ONLY: Available industries for testing
    const testIndustries = [
        "Technology",
        "Healthcare",
        "Finance",
        "Retail",
        "Manufacturing",
        "Education",
        "Real Estate",
        "Legal",
        "Consulting",
        "Media & Entertainment",
        "Transportation",
        "Agriculture",
        "Non-profit",
        "Government",
        "Other",
    ];

    // Web Apps configuration
    const webApps = [
        {
            key: "clientProjectManager",
            icon: "Briefcase",
            color: "blue",
        },
        {
            key: "salesLeadsTracker",
            icon: "TrendingUp",
            color: "green",
        },
        {
            key: "expenseInvoiceDashboard",
            icon: "Receipt",
            color: "purple",
        },
        {
            key: "eventRegistrationManager",
            icon: "Calendar",
            color: "orange",
        },
    ];

    // Websites configuration
    const websites = [
        {
            key: "startupMarketing",
            icon: "Rocket",
            color: "cyan",
        },
        {
            key: "consultantPortfolio",
            icon: "User",
            color: "pink",
        },
        {
            key: "onlineStore",
            icon: "ShoppingCart",
            color: "teal",
        },
        {
            key: "interiorDesigner",
            icon: "Palette",
            color: "indigo",
        },
    ];

    // Dashboards configuration
    const dashboards = [
        {
            key: "reportingDashboard",
            icon: "BarChart3",
            color: "blue",
        },
        {
            key: "salesDashboard",
            icon: "TrendingUp",
            color: "green",
        },
        {
            key: "onboardingPortal",
            icon: "Users",
            color: "purple",
        },
        {
            key: "saasFounderKPIDashboard",
            icon: "Activity",
            color: "orange",
        },
    ];

    const colorConfigs = {
        blue: {
            gradient: "from-blue-50 to-indigo-50",
            border: "border-blue-200",
            iconBg: "bg-blue-100",
            iconText: "text-blue-600",
            textColor: "text-blue-600",
        },
        green: {
            gradient: "from-green-50 to-emerald-50",
            border: "border-green-200",
            iconBg: "bg-green-100",
            iconText: "text-green-600",
            textColor: "text-green-600",
        },
        purple: {
            gradient: "from-purple-50 to-violet-50",
            border: "border-purple-200",
            iconBg: "bg-purple-100",
            iconText: "text-purple-600",
            textColor: "text-purple-600",
        },
        orange: {
            gradient: "from-orange-50 to-amber-50",
            border: "border-orange-200",
            iconBg: "bg-orange-100",
            iconText: "text-orange-600",
            textColor: "text-orange-600",
        },
        cyan: {
            gradient: "from-cyan-50 to-blue-50",
            border: "border-cyan-200",
            iconBg: "bg-cyan-100",
            iconText: "text-cyan-600",
            textColor: "text-cyan-600",
        },
        pink: {
            gradient: "from-pink-50 to-rose-50",
            border: "border-pink-200",
            iconBg: "bg-pink-100",
            iconText: "text-pink-600",
            textColor: "text-pink-600",
        },
        teal: {
            gradient: "from-teal-50 to-cyan-50",
            border: "border-teal-200",
            iconBg: "bg-teal-100",
            iconText: "text-teal-600",
            textColor: "text-teal-600",
        },
        indigo: {
            gradient: "from-indigo-50 to-purple-50",
            border: "border-indigo-200",
            iconBg: "bg-indigo-100",
            iconText: "text-indigo-600",
            textColor: "text-indigo-600",
        },
    };

    const renderCard = (item, category) => {
        const colors = colorConfigs[item.color] || colorConfigs.blue;
        const translationKey = `${basePath}.${category}.${mappedIndustry}.${item.key}`;

        const fallbackKey =
            category === "webApp"
                ? `chatPage.webApp.${item.key}`
                : category === "website"
                ? `chatPage.website.${item.key}`
                : `chatPage.prompts.${item.key}`;

        return (
            <div
                key={item.key}
                className={`bg-gradient-to-br ${colors.gradient} ${colors.border} rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group`}
                onClick={() =>
                    onPromptClick(
                        t(`${translationKey}.prompt`, {
                            defaultValue: t(`${fallbackKey}.prompt`),
                        })
                    )
                }
            >
                <div className="flex items-start gap-3">
                    <div
                        className={`${colors.iconBg} p-2 rounded-lg group-hover:opacity-80 transition-colors`}
                    >
                        <Icon
                            name={item.icon}
                            className={`h-5 w-5 ${colors.iconText}`}
                        />
                    </div>
                    <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                            {t(`${translationKey}.title`, {
                                defaultValue: t(`${fallbackKey}.title`),
                            })}
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                            {t(`${translationKey}.description`, {
                                defaultValue: t(`${fallbackKey}.description`),
                            })}
                        </p>
                        <div
                            className={`text-xs ${colors.textColor} font-medium`}
                        >
                            {t(
                                category === "webApp"
                                    ? "chatPage.webApp.clickToCreate"
                                    : category === "website"
                                    ? "chatPage.website.clickToCreate"
                                    : "chatPage.prompts.reportingDashboard.clickToUse"
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8">
            {/* TEST ONLY: Industry Switcher - Remove after testing */}
            {/* <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-800">
                        TEST ONLY: Switch Industry
                    </span>
                    {testIndustry && (
                        <button
                            onClick={() => setTestIndustry(null)}
                            className="text-xs text-yellow-600 hover:text-yellow-800 underline"
                        >
                            Reset to User Industry
                        </button>
                    )}
                </div>
                <div className="flex flex-wrap gap-2">
                    {testIndustries.map((ind) => (
                        <button
                            key={ind}
                            onClick={() => setTestIndustry(ind)}
                            className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
                                testIndustry === ind
                                    ? "bg-yellow-200 text-yellow-900 border-yellow-400 font-medium"
                                    : "bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                            }`}
                        >
                            {ind}
                        </button>
                    ))}
                </div>
                {testIndustry && (
                    <div className="mt-2 text-xs text-yellow-700">
                        Testing with: <strong>{testIndustry}</strong> (User
                        industry: {user?.industry || "other"})
                    </div>
                )}
            </div> */}

            {/* Web App Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {t(`${basePath}.webApp.${mappedIndustry}.title`, {
                        defaultValue: t("chatPage.webApp.title"),
                    })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {webApps.map((app) => renderCard(app, "webApp"))}
                </div>
            </div>

            {/* Website Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {t(`${basePath}.website.${mappedIndustry}.title`, {
                        defaultValue: t("chatPage.website.title"),
                    })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {websites.map((site) => renderCard(site, "website"))}
                </div>
            </div>

            {/* Dashboards Section */}
            <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {t(`${basePath}.dashboards.${mappedIndustry}.title`, {
                        defaultValue: t("chatPage.dashboards.title"),
                    })}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dashboards.map((dashboard) =>
                        renderCard(dashboard, "dashboards")
                    )}
                </div>
            </div>
        </div>
    );
}

// Wrap with error boundary to prevent crashes
function UserTypeDashboardsWithErrorBoundary(props) {
    return (
        <UserTypeDashboardsErrorBoundary>
            <UserTypeDashboards {...props} />
        </UserTypeDashboardsErrorBoundary>
    );
}

export default UserTypeDashboardsWithErrorBoundary;
